/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line simple-import-sort/imports
import { useRef, useState } from 'react';
import { CarDetailHeaderNavBarItem } from '@apps/components/CarDetails/CarDetailHeaderNavBarItem';
import { CarDetailNavItemRefProps } from '@apps/components/CarDetails/CarDetailHeaderNavBarItem/props';
import { CarDetailSectionContent } from '@apps/components/CarDetails/CarDetailSectionContent';
import {
  ActiveNavItemsType,
  CarDetailSectionContentRefProps,
} from '@apps/components/CarDetails/CarDetailSectionContent/props';
import { SpecAndFeatureDataType } from '@apps/components/CarDetails/SpecAndFeature';
import StickyCarDetailCard from '@apps/components/StickyCarDetailCard';
import { useScrollDirection } from '@apps/config/windowscroll.config';
import Footer from '@apps/layouts/Footer';
import Navbar from '@apps/layouts/Navbar';
import { CategoriesBoxWrapper } from '@apps/layouts/Navbar/styles';
import Box from '@mui/material/Box';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { Breadcrumb } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { isEmpty } from 'lodash';
import { useAmp } from 'next/amp';
import { PagesProgressBar as ProgressBar } from 'next-nprogress-bar';
import { StickyCarDetailCardRef } from '@apps/components/StickyCarDetailCard/props';

export type CarDetailProps = {
  webView: boolean;
  data: any;
  settings: any;
  //any
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export interface SectionHandle {
  scrollToSection: (key: string) => void;
  container: HTMLDivElement | null;
}

export const CarDetailScreen: React.FC<CarDetailProps> = (props) => {
  const { data, settings, webView } = props;

  // ======================= HOOKS
  const { basePath } = createBasePath({ webView, type: 'cars' });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navHeaderRef = useRef<HTMLDivElement | null>(null);
  const stickyCarDetailRef = useRef<StickyCarDetailCardRef>(null);
  const sectionRef = useRef<CarDetailSectionContentRefProps>(null);
  const navItemRef = useRef<CarDetailNavItemRefProps>(null);
  const [activeNavItems, setActiveNavItems] = useState<ActiveNavItemsType>([]);
  const scrollDirection = useScrollDirection();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedVariant, setSelectedVariant] =
    useState<SpecAndFeatureDataType>();
  const isAmp = useAmp();

  // ======================= EVENTS

  const handleClick = (event: React.MouseEvent<HTMLElement>, key: string) => {
    event.preventDefault();
    // TODO if possible make it direct focused the selected menu, instead of focus the menu one by one
    // navItemRef.current?.setFocusedNavItem(key);
    sectionRef.current?.scrollToSection(key);
  };

  // ======================= VARIABLES
  const carDetail = data?.data;

  // ======================= VIEWS
  const renderNavItem = () => {
    if (isEmpty(activeNavItems)) return <div></div>;
    return (
      <div
        id="carDetailHeader"
        style={{
          position: 'sticky',
          backgroundColor: 'white',
          top: webView ? 0 : 50,
          zIndex: 1000,
        }}
      >
        <CategoriesBoxWrapper $show={scrollDirection !== 'down'}>
          <CarDetailHeaderNavBarItem
            ref={navItemRef}
            handleClick={handleClick}
            activeNavItems={activeNavItems}
          />
        </CategoriesBoxWrapper>
      </div>
    );
  };

  // ======================= BREADCRUMB PATH
  const breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: 'AutoFacts',
      url: `${basePath === '' ? '/' : basePath}`,
    },
    {
      title: carDetail?.car_brand_name || '-',
      url: `${basePath}/${carDetail?.car_brand_slug}`,
    },
    {
      title: carDetail?.new_car_title || '-',
      url: '',
    },
  ];

  // ======================= VIEW
  return (
    <Box
      ref={containerRef}
      sx={{
        maxHeight: '100vh',
        overflowY: 'auto',
        width: '100%',
      }}
    >
      {/* Nav Bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2000,
          backgroundColor: '#FFFFFF',
          overflow: 'none',
        }}
        ref={navHeaderRef}
      >
        <Navbar
          pageProps={{ settings }}
          webView={webView}
          containerNoCss
          // config={{ renderNavItems: renderNavItem() }}
        />
      </Box>

      {/* Progress Bar */}
      {!isAmp && (
        <ProgressBar
          height="3px"
          color="#31acbf"
          options={{ showSpinner: false }}
          shallowRouting
        />
      )}

      {/* BREADCRUMB */}
      {!webView && (
        <div
          style={{
            paddingTop: '20px',
            display: 'flex',
            flexDirection: 'row',
            margin: '0 auto',
            maxWidth: 1240,
            padding: '20px 0 0 20px',
          }}
        >
          <Breadcrumb options={breadcrumbOpt} bold />
        </div>
      )}

      {renderNavItem()}

      {/* Body Content */}
      <main
        className="body-wrapper"
        style={{
          overflow: 'hidden',
          ...(webView && { paddingTop: 0, paddingBottom: 160 }),
        }}
      >
        {/* MAIN CONTENT */}
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
          }}
          sx={{ marginTop: { xs: 0, md: '20px' } }}
          id={'carDetailBody'}
        >
          <CarDetailSectionContent
            ref={sectionRef}
            containerRef={containerRef}
            navHeaderRef={navHeaderRef}
            navItemRef={navItemRef}
            stickyCarDetailRef={stickyCarDetailRef}
            data={data}
            settings={settings}
            activeNavItems={activeNavItems}
            selectedVariant={selectedVariant}
            webView={webView}
            setSelectedVariant={setSelectedVariant}
            onSectionsCallback={(items) => {
              setActiveNavItems(items);
            }}
          />

          <StickyCarDetailCard
            data={data.data}
            webView={webView}
            location_city={settings.location_city}
            location_state={settings.location_state}
            ref={stickyCarDetailRef}
          />
        </Box>
      </main>

      {/* Footer */}
      {!webView && <Footer pageProps={{ settings }} webView={webView} />}
    </Box>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default CarDetailScreen;
